/**
 * AutomationRuleRowView — pure presentation for one rule in the Rules panel
 * (SE-5506).
 *
 * Shows the rule name, a plain-language summary (the same client-side preview
 * the builder renders), a paused badge when disabled, and the three per-rule
 * controls the panel AC requires: an enable/pause Switch, Edit, and Delete.
 * @remarks View component — no business logic, hooks, or state.
 */

import { Pencil, Trash2 } from "lucide-react-native";
import { memo } from "react";

import { Badge, BadgeText } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Switch } from "@/components/atoms/Switch";
import { Text } from "@/components/atoms/Text";

/** Props for {@link AutomationRuleRowView}. */
export interface AutomationRuleRowViewProps {
  /** Rule display name. */
  readonly name: string;
  /** Plain-language summary of what the rule does. */
  readonly summary: string;
  /** Whether the rule is enabled (Switch value). */
  readonly isEnabled: boolean;
  /** Whether a write for this rule is in flight (disables controls). */
  readonly isBusy: boolean;
  /** Enable/pause handler. */
  readonly onToggleEnabled: (value: boolean) => void;
  /** Edit handler. */
  readonly onEdit: () => void;
  /** Delete handler. */
  readonly onDelete: () => void;
}

/**
 * Render one rule row.
 * @param props - {@link AutomationRuleRowViewProps}
 * @param props.name - Rule display name
 * @param props.summary - Plain-language summary
 * @param props.isEnabled - Whether the rule is enabled
 * @param props.isBusy - Whether a write is in flight
 * @param props.onToggleEnabled - Enable/pause handler
 * @param props.onEdit - Edit handler
 * @param props.onDelete - Delete handler
 * @returns The rule row.
 */
const AutomationRuleRowView = ({
  name,
  summary,
  isEnabled,
  isBusy,
  onToggleEnabled,
  onEdit,
  onDelete,
}: AutomationRuleRowViewProps) => (
  <Box
    testID="automation-rule-row"
    className="gap-2 rounded-md border border-outline-subtle p-3"
  >
    <Stack
      direction="horizontal"
      className="items-center justify-between gap-2"
    >
      <Stack direction="horizontal" className="flex-1 items-center gap-2">
        <Text
          variant="body-strong"
          className="flex-1 text-content-primary"
          numberOfLines={1}
        >
          {name}
        </Text>
        {isEnabled ? null : (
          <Badge testID="automation-rule-paused" action="muted">
            <BadgeText>Paused</BadgeText>
          </Badge>
        )}
      </Stack>
      <Switch
        size="sm"
        value={isEnabled}
        onValueChange={onToggleEnabled}
        isDisabled={isBusy}
        testID="automation-rule-enabled"
      />
    </Stack>
    <Text variant="caption" className="text-content-secondary">
      {summary}
    </Text>
    <Stack direction="horizontal" className="items-center justify-end gap-4">
      <Pressable
        onPress={onEdit}
        accessibilityLabel={`Edit ${name}`}
        testID="automation-rule-edit"
        disabled={isBusy}
      >
        <Stack direction="horizontal" className="items-center gap-1">
          <Icon as={Pencil} size="sm" className="text-content-secondary" />
          <Text variant="caption" className="text-content-secondary">
            Edit
          </Text>
        </Stack>
      </Pressable>
      <Pressable
        onPress={onDelete}
        accessibilityLabel={`Delete ${name}`}
        testID="automation-rule-delete"
        disabled={isBusy}
      >
        <Stack direction="horizontal" className="items-center gap-1">
          <Icon as={Trash2} size="sm" className="text-status-error" />
          <Text variant="caption" className="text-status-error">
            Delete
          </Text>
        </Stack>
      </Pressable>
    </Stack>
  </Box>
);

AutomationRuleRowView.displayName = "AutomationRuleRowView";

export default memo(AutomationRuleRowView);
